import type { ReactNode } from 'react';
import { Title } from '../../../../../components/Toolkit/InfoTitle/Title/Title';
import { media } from '../../../../../helpers/breakpoints';
import styled from 'styled-components';
import { Notification } from '../../../../../components/Toolkit/Notification/Notification';
import { GhostButton } from '../../../../../components/Toolkit/Button';
import { LinkButton } from '../../../../../components/Toolkit/Button/LinkButton';
import { BarChartIc as BaseBarChartIc } from '@dsch/dd-icons';

const Header = styled.div`
  margin-bottom: ${({ theme }) => theme.donedeal.spacing.S8};

  ${media.large} {
    margin-bottom: ${({ theme }) => theme.donedeal.spacing.S8};
  }
`;

const AdTitle = styled(Title)`
  max-height: 48px;
  margin-bottom: 0;
  overflow: hidden;
  font-weight: ${({ theme }) => theme.donedeal.fontWeight.regular};
`;

const TitleAndUserActionsContainer = styled.div`
  display: flex;
  justify-content: space-between;
`;

const PriceAndActionsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  align-items: center;

  margin-bottom: ${({ theme }) => theme.donedeal.spacing.M16};
`;

const Price = styled.div<{
  isPriceOnRequest?: boolean;
}>`
  ${({ theme, isPriceOnRequest }) =>
    isPriceOnRequest
      ? theme.donedeal.fontSize.L18
      : theme.donedeal.fontSize.H24};
  color: ${({ theme }) => theme.donedeal.colors.GREY_DARKER};
  font-weight: ${({ theme }) => theme.donedeal.fontWeight.regular};
  margin-bottom: 0;
`;

const OldPrice = styled.span`
  ${({ theme }) => theme.donedeal.fontSize.M14};
  color: ${({ theme }) => theme.donedeal.colors.GREY_DARK};
  position: relative;
  display: inline;

  &:after {
    position: absolute;
    content: '';
    left: 0;
    top: 50%;
    right: 0;
    border-top: 1px solid;
    border-color: inherit;
    transform: rotate(-12.2deg);
  }
`;

// TODO: Should the dashed underline and relevant hover styles be baked into link button?

const PricePerMonthButton = styled(LinkButton)`
  align-self: flex-start;
  font-weight: ${({ theme }) => theme.donedeal.fontWeight.regular};
  &:hover,
  &:focus-visible {
    text-decoration: none;
  }
  border-bottom: 1px dashed ${({ theme }) => theme.donedeal.colors.GREY};
`;

const PricePerMonthText = styled.span`
  ${({ theme }) => theme.donedeal.fontSize.M14};
  color: ${({ theme }) => theme.donedeal.colors.GREY_DARK};
`;

const PriceAndDeliveryContainer = styled.div`
  display: flex;
  flex-direction: column;
  order: 1;
`;

const PriceContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const PriceRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-end;
  gap: ${({ theme }) => theme.donedeal.spacing.S8};
`;

const Actions = styled.div<{
  isViewingOwnAd: boolean;
}>`
  position: relative;
  order: 2;
  margin-bottom: 0;

  ${media.medium} {
    margin-bottom: 0;
    order: 3;
  }
`;

const PositionedActions = styled.div`
  display: flex;
`;

const OwnAdButtonContainer = styled.div<{ renderBoostButton?: ReactNode }>`
  display: none;

  ${media.medium} {
    position: relative;
    top: 0;
    right: 0;
    display: grid;
    grid-template-columns: ${({ renderBoostButton }) =>
      renderBoostButton === undefined
        ? `repeat(1, 140px)`
        : `repeat(2, 140px)`};
    grid-column-gap: ${({ theme }) => theme.donedeal.spacing.S8};
  }
`;

const ShareAdContainer = styled.div<{
  isViewingOwnAd: boolean;
}>`
  ${media.medium} {
    margin-right: ${({ isViewingOwnAd, theme }) =>
      !isViewingOwnAd ? theme.donedeal.spacing.M16 : '0'};
  }
`;

const SoldTag = styled.div`
  margin-bottom: ${({ theme }) => theme.donedeal.spacing.S8};
`;

const FinanceLinkWrapper = styled.div`
  order: 3;
  flex: 1 1 100%;
  font-family: 'Lato', sans-serif;
  display: flex;
  ${media.medium} {
    order: 4;
  }
`;

export const NextToPriceContainer = styled.div`
  border-left: solid ${({ theme }) => theme.donedeal.colors.GREY_LIGHT} 1px;
  padding-left: ${({ theme }) => theme.donedeal.spacing.M16};
  margin-left: ${({ theme }) => theme.donedeal.spacing.M16};
  width: 115px;
`;

const OuterWrapper = styled.div`
  display: flex;
  order: 3;
  flex: 1 1 100%;

  ${media.medium} {
    order: 2;
    margin-left: ${({ theme }) => theme.donedeal.spacing.M32};
    flex: 1 1 auto;
  }
`;

const InnerWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  flex-grow: 1;
  position: relative;

  ${media.medium} {
    flex-wrap: nowrap;
    padding-top: ${({ theme }) => theme.donedeal.spacing.S4};
    flex-grow: 0;
  }
`;

const SuccessWrapper = styled.div`
  width: 100%;
`;

const SNotification = styled(Notification)`
  box-shadow: ${({ theme }) => theme.donedeal.shadow.CONTAINER};
  padding: ${({ theme }) => theme.donedeal.spacing.S8};

  ${media.medium} {
    padding: ${({ theme }) =>
      `${theme.donedeal.spacing.S8} ${theme.donedeal.spacing.M16} ${theme.donedeal.spacing.S8} ${theme.donedeal.spacing.S12}`};
  }
`;

const AnalyticsContainer = styled.div`
  ${({ theme }) => theme.donedeal.fontSize.M14};

  ${media.medium} {
    width: 163px;
  }
`;

const AnalyticsButton = styled(GhostButton)`
  margin-right: 0;

  ${media.medium} {
    margin-right: ${({ theme }) => theme.donedeal.spacing.M16};
  }
`;

const BarChartIc = styled(BaseBarChartIc)`
  ${media.medium} {
    width: 20px;
    height: 20px;
  }
`;

const AnalyticsTextContainer = styled.p`
  display: none;

  ${media.medium} {
    display: block;
    margin-left: ${({ theme }) => theme.donedeal.spacing.S4};
    color: ${({ theme }) => theme.donedeal.colors.BLUE};
  }
`;

export {
  Header,
  AdTitle,
  TitleAndUserActionsContainer,
  PriceAndActionsContainer,
  PriceContainer,
  PriceRow,
  PriceAndDeliveryContainer,
  Price,
  OldPrice,
  PricePerMonthButton,
  PricePerMonthText,
  Actions,
  PositionedActions,
  OwnAdButtonContainer,
  ShareAdContainer,
  SoldTag,
  FinanceLinkWrapper,
  OuterWrapper,
  InnerWrapper,
  SuccessWrapper,
  SNotification,
  AnalyticsContainer,
  AnalyticsTextContainer,
  AnalyticsButton,
  BarChartIc,
};
